import { useState, useCallback } from 'react';
import { api } from '../utils/api';
import type { VocabularyAnswerResult, VocabularyExercise, VocabularyProgress, VocabularyWord } from '@homeapp/shared';

interface VocabularyProgressOverview {
  totalWords: number;
  practiced: number;
  totalCorrect: number;
  totalIncorrect: number;
  accuracy: number;
  words: Array<{
    wordId: string;
    word: string;
    topic: string;
    level: number;
    progress: VocabularyProgress | null;
  }>;
}

export function useVocabularyTrainer() {
  const [exercise, setExercise] = useState<VocabularyExercise | null>(null);
  const [progressOverview, setProgressOverview] = useState<VocabularyProgressOverview | null>(null);
  const [words, setWords] = useState<(VocabularyWord & { progress?: VocabularyProgress })[]>([]);
  const [loading, setLoading] = useState(false);
  const [exerciseLoading, setExerciseLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<VocabularyAnswerResult | null>(null);

  const fetchWords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<{ words: (VocabularyWord & { progress?: VocabularyProgress })[] }>('/vocabulary/list');
      setWords(data.words);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load words');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<VocabularyProgressOverview>('/vocabulary/progress');
      setProgressOverview(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, []);

  const nextExercise = useCallback(async () => {
    setExerciseLoading(true);
    setError(null);
    setLastResult(null);
    try {
      const data = await api.get<{ exercise: VocabularyExercise | null; message?: string }>('/vocabulary/exercise');
      setExercise(data.exercise);
      if (!data.exercise && data.message) setError(data.message);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load exercise');
    } finally {
      setExerciseLoading(false);
    }
  }, []);

  const submitAnswer = useCallback(async (wordId: string, selectedText: string) => {
    try {
      const data = await api.post<VocabularyAnswerResult>(`/vocabulary/exercise/${wordId}/answer`, { selectedText });
      setLastResult(data);
      return data;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit answer');
      return null;
    }
  }, []);

  const createWord = useCallback(async (word: Partial<VocabularyWord>) => {
    try {
      const data = await api.post<{ word: VocabularyWord }>('/vocabulary/words', { word });
      return data.word;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create word');
      return null;
    }
  }, []);

  const updateWord = useCallback(async (wordId: string, word: Partial<VocabularyWord>) => {
    try {
      const data = await api.put<{ word: VocabularyWord }>(`/vocabulary/words/${wordId}`, { word });
      return data.word;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update word');
      return null;
    }
  }, []);

  const deleteWord = useCallback(async (wordId: string) => {
    try {
      await api.delete(`/vocabulary/words/${wordId}`);
      setWords(prev => prev.filter(w => w.wordId !== wordId));
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete word');
      return false;
    }
  }, []);

  const generateBatch = useCallback(async (payload: {
    topic: string;
    level: number;
    count: number;
    partOfSpeech: string;
    seedWords: string[];
  }) => {
    try {
      const data = await api.post<{
        createdCount: number;
        skippedExisting: number;
        skippedInvalid: number;
        words: VocabularyWord[];
      }>('/vocabulary/generate-batch', payload);
      return data;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to generate vocabulary batch');
      return null;
    }
  }, []);

  return {
    exercise,
    progressOverview,
    words,
    loading,
    exerciseLoading,
    error,
    lastResult,
    fetchWords,
    fetchProgress,
    nextExercise,
    submitAnswer,
    createWord,
    updateWord,
    deleteWord,
    generateBatch,
    setError,
  };
}
